import { useState, useEffect } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Bell, MessageSquare, Mail, Save, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
const NOTIFICATION_TYPES = [
  { key: "missing_reports", label: "Missing Daily Reports", description: "Alerts when jobs are missing daily reports", roles: "all" },
  { key: "backup_reminder", label: "Backup Reminder", description: "Reminder when database hasn't been backed up", roles: "managers" },
  { key: "bid_due", label: "Bid Due Dates", description: "Alerts for upcoming bid deadlines", roles: "managers" },
  { key: "billing_due", label: "Billing Due Dates", description: "Alerts for billing payment deadlines", roles: "managers" },
  { key: "invoice_due", label: "Invoice Due Dates", description: "Alerts for invoice payment deadlines", roles: "managers" },
  { key: "note_due", label: "Follow-Up Reminders", description: "Alerts for notes and follow-ups that are due", roles: "managers" },
  { key: "training_expiration", label: "Training Certificate Expiration", description: "Alerts when employee certificates are expiring or expired", roles: "managers" },
];

export const NotificationPreferencesTab = ({ getAuthHeaders, userRole }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasEmail, setHasEmail] = useState(false);
  
  // Global toggles
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [appEnabled, setAppEnabled] = useState(true);
  
  // Per-notification preferences
  const [notificationTypes, setNotificationTypes] = useState({});

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await apiClient.get(`/users/me/notification-preferences`);
        
        const prefs = response.data;
        setEmailEnabled(prefs.email_enabled);
        setAppEnabled(prefs.app_enabled);
        setNotificationTypes(prefs.notification_types || {});
        setHasEmail(prefs.has_email);
      } catch (error) {
        toast.error("Failed to load notification preferences");
      } finally {
        setLoading(false);
      }
    };
    
    fetchPreferences();
  }, [getAuthHeaders]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put(
        `/users/me/notification-preferences`,
        {
          email_enabled: emailEnabled,
          app_enabled: appEnabled,
          notification_types: notificationTypes,
        }
      );
      toast.success("Notification preferences saved");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const updateNotificationType = (typeKey, channel, value) => {
    setNotificationTypes(prev => ({
      ...prev,
      [typeKey]: {
        ...prev[typeKey],
        [channel]: value,
      },
    }));
  };

  const getTypePreference = (typeKey, channel) => {
    return notificationTypes[typeKey]?.[channel] ?? true;
  };

  // Determine which notification types this user role can configure
  const availableTypes = NOTIFICATION_TYPES.filter(type => {
    if (userRole === "foreman" || userRole === "superintendent") {
      return type.key === "missing_reports";
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-pulse text-muted-foreground">Loading preferences...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Contact Info Alert */}
      {!hasEmail && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            You don't have an email address on file. Ask an admin to update your profile to receive email notifications.
          </AlertDescription>
        </Alert>
      )}

      {/* Global Toggles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notification Channels
          </CardTitle>
          <CardDescription>Choose how you want to receive notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-blue-600" />
              <div>
                <Label className="text-base">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  {hasEmail ? "Receive email alerts" : "No email address on file"}
                </p>
              </div>
            </div>
            <Switch
              checked={emailEnabled}
              onCheckedChange={setEmailEnabled}
              disabled={!hasEmail}
              data-testid="email-toggle"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              <div>
                <Label className="text-base">In-App Notifications</Label>
                <p className="text-sm text-muted-foreground">See alerts within the app</p>
              </div>
            </div>
            <Switch
              checked={appEnabled}
              onCheckedChange={setAppEnabled}
              data-testid="app-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Per-Notification Type Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Types</CardTitle>
          <CardDescription>Fine-tune which notifications you receive and how</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {availableTypes.map((type) => (
              <div key={type.key} className="border rounded-lg p-4">
                <div className="mb-3">
                  <h4 className="font-medium">{type.label}</h4>
                  <p className="text-sm text-muted-foreground">{type.description}</p>
                </div>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={getTypePreference(type.key, "email")}
                      onCheckedChange={(v) => updateNotificationType(type.key, "email", v)}
                      disabled={!hasEmail || !emailEnabled}
                      data-testid={`${type.key}-email`}
                    />
                    <Label className="text-sm">Email</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={getTypePreference(type.key, "app")}
                      onCheckedChange={(v) => updateNotificationType(type.key, "app", v)}
                      disabled={!appEnabled}
                      data-testid={`${type.key}-app`}
                    />
                    <Label className="text-sm">In-App</Label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-notification-prefs">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Preferences"}
        </Button>
      </div>
    </div>
  );
};

export default NotificationPreferencesTab;
